import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export const normalizePaymentEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeBookingName = (value) => String(value || "").trim();

const getMappedProviderName = (mapping) =>
  mapping ? normalizeBookingName(mapping.bookingName) : "";

export const isPaymentAccessOwner = (user) =>
  String((user && user.displayName) || "")
    .trim()
    .toLowerCase() === "admin888";

export const usePaymentAccessScope = (providerRole) => {
  const { currentUser } = useAuth();
  const [scope, setScope] = useState({
    loading: true,
    paymentAccess: false,
    providerName: "",
    mapping: null,
  });

  useEffect(() => {
    if (!currentUser) {
      setScope({
        loading: false,
        paymentAccess: false,
        providerName: "",
        mapping: null,
      });
      return undefined;
    }

    const currentRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [];
    const isOwner = isPaymentAccessOwner(currentUser);
    const hasProviderRole = currentRoles.includes(providerRole);
    const hasSuperAdminRole = currentRoles.includes("super_admin");
    // A super admin without full payment access may use a single provider
    // scope, but never receives the all-provider payment scope from this role.
    const canUseAccountProviderScope =
      Boolean(providerRole) && (hasProviderRole || hasSuperAdminRole);
    const email = normalizePaymentEmail(currentUser.email);

    let accessLoaded = isOwner || !email;
    let mappingLoaded = isOwner || !email;
    let accessDocument = null;
    let mapping = null;

    const syncScope = () => {
      if (!accessLoaded || !mappingLoaded) return;

      const paymentAccess = isOwner || Boolean(accessDocument);
      const configuredProviderName = getMappedProviderName(mapping);
      // An explicit email mapping is the authority for scoped access. Neither
      // its saved booking provider type nor the account's custom role limits
      // whether that exact booking name can be used.
      const hasMappedProviderScope = Boolean(configuredProviderName);
      const providerName = paymentAccess
        ? ""
        : hasMappedProviderScope
          ? configuredProviderName
          : canUseAccountProviderScope && !mapping
            ? normalizeBookingName(currentUser.displayName)
          : "";

      setScope({
        loading: false,
        paymentAccess,
        providerName,
        mapping,
      });
    };

    const unsubscribeAccess = !isOwner && email
      ? onSnapshot(
          doc(db, "paymentAccessEmails", email),
          (snapshot) => {
            accessDocument = snapshot.exists() ? snapshot.data() || {} : null;
            accessLoaded = true;
            syncScope();
          },
          (error) => {
            console.error("Unable to load payment access email", error);
            accessLoaded = true;
            syncScope();
          },
        )
      : () => {};

    const unsubscribeMapping = !isOwner && email
      ? onSnapshot(
          doc(db, "paymentUserMappings", email),
          (snapshot) => {
            mapping = snapshot.exists() ? snapshot.data() || {} : null;
            mappingLoaded = true;
            syncScope();
          },
          (error) => {
            console.error("Unable to load payment user mapping", error);
            mappingLoaded = true;
            syncScope();
          },
        )
      : () => {};

    syncScope();

    return () => {
      unsubscribeAccess();
      unsubscribeMapping();
    };
  }, [currentUser, providerRole]);

  const currentRoles = Array.isArray(currentUser && currentUser.roles)
    ? currentUser.roles
    : [];
  const hasProviderRole = currentRoles.includes(providerRole);
  const hasSuperAdminRole = currentRoles.includes("super_admin");
  const canUseAccountProviderScope =
    Boolean(providerRole) && (hasProviderRole || hasSuperAdminRole);
  const hasMappedProviderScope = Boolean(
    getMappedProviderName(scope.mapping),
  );

  return {
    ...scope,
    hasProviderRole,
    hasSuperAdminRole,
    canManagePayments:
      scope.paymentAccess ||
      hasMappedProviderScope ||
      (canUseAccountProviderScope && Boolean(scope.providerName)),
  };
};
